/**
 * ModulEinstellungen — Generic settings component for module-level configuration.
 *
 * Reads system_config keys by category and renders them as editable form rows.
 * Auto-renders based on type: toggle, number, text, select, multiselect.
 *
 * @component
 * @param {string[]} kategorien - system_config categories to display
 * @param {string[]} [filterKeys] - Only show these keys (optional)
 * @param {string[]} [excludeKeys] - Hide these keys (optional)
 * @param {{ label: string, keys?: string[], categories?: string[] }[]} [gruppen] - Group settings into cards
 * @since v2.226.0
 */
import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ToggleSwitch } from './ui/ToggleSwitch';
import { Save, Loader2, CheckCircle } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

export default function ModulEinstellungen({ kategorien = [], filterKeys, excludeKeys, gruppen }) {
  const { t } = useLanguage();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  useEffect(() => {
    if (!kategorien.length) return;
    setLoading(true);
    fetch(`${API}/api/einstellungen/kategorie/${kategorien.join(',')}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        let items = d.settings || [];
        if (filterKeys?.length) items = items.filter(s => filterKeys.includes(s.key));
        if (excludeKeys?.length) items = items.filter(s => !excludeKeys.includes(s.key));
        setSettings(items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [kategorien.join(',')]);

  const handleChange = useCallback(async (key, newValue) => {
    setSaving(key);
    try {
      const res = await fetch(`${API}/api/einstellungen/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: newValue }),
      });
      if (res.ok) {
        setSettings(prev => prev.map(s => s.key === key ? { ...s, value: newValue } : s));
        showToast(t('modulSettings.saved', 'Einstellung gespeichert'));
      }
    } catch {}
    setSaving(null);
  }, [t, showToast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-[var(--text-tertiary)]" />
      </div>
    );
  }

  if (!settings.length) {
    return (
      <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">
        {t('modulSettings.noSettings', 'Keine Einstellungen verfügbar')}
      </div>
    );
  }

  // Group settings into cards
  const renderGroup = (items, label) => (
    <div key={label || 'default'} className="border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)]">
      {label && (
        <div className="px-5 py-3 border-b" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{label}</h3>
        </div>
      )}
      <div className="px-5">
        {items.map(s => (
          <SettingRow key={s.key} setting={s} onChange={handleChange} saving={saving} t={t} />
        ))}
      </div>
    </div>
  );

  const groupedContent = () => {
    if (gruppen?.length) {
      return gruppen.map(g => {
        const items = settings.filter(s =>
          (g.keys ? g.keys.includes(s.key) : true) &&
          (g.categories ? g.categories.includes(s.category) : true)
        );
        if (!items.length) return null;
        return renderGroup(items, g.label);
      }).filter(Boolean);
    }
    // Default: group by category
    const byCategory = {};
    for (const s of settings) {
      const cat = s.category || 'other';
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(s);
    }
    const cats = Object.keys(byCategory);
    if (cats.length === 1) return [renderGroup(byCategory[cats[0]], null)];
    return cats.map(cat => renderGroup(byCategory[cat], cat));
  };

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed top-3 right-4 z-[100]">
          <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl px-4 py-2.5 flex items-center gap-2 bg-[var(--bg-card,#fff)] shadow-sm">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span className="text-xs" style={{ color: 'var(--text-secondary, #4B5563)' }}>{toast}</span>
          </div>
        </div>
      )}
      {groupedContent()}
    </div>
  );
}

function SettingRow({ setting, onChange, saving, t }) {
  const { key, value, label, type, options, description } = setting;
  const isSaving = saving === key;

  return (
    <div className="flex items-start justify-between gap-6 py-4 border-b last:border-0" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{label || key}</div>
        {description && <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary, #4B5563)' }}>{description}</div>}
      </div>
      <div className="flex-shrink-0 w-56">
        {type === 'toggle' ? (
          <ToggleSwitch checked={!!value} onChange={() => onChange(key, !value)} disabled={isSaving} />
        ) : type === 'select' ? (
          <select
            value={typeof value === 'string' ? value : JSON.stringify(value)}
            onChange={(e) => onChange(key, e.target.value)}
            disabled={isSaving}
            className="w-full px-3 py-1.5 text-sm rounded-lg border"
            style={{ borderColor: 'var(--border-default, #E5E7EB)', background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
          >
            {(options || []).map(opt => (
              <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
            ))}
          </select>
        ) : type === 'number' ? (
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(key, Number(e.target.value))}
            disabled={isSaving}
            className="w-full px-3 py-1.5 text-sm rounded-lg border"
            style={{ borderColor: 'var(--border-default, #E5E7EB)', background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
          />
        ) : type === 'multiselect' ? (
          <div className="flex flex-wrap gap-1.5">
            {(options || []).map(opt => {
              const selected = Array.isArray(value) && value.includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => {
                    const next = selected ? (value || []).filter(v => v !== opt) : [...(value || []), opt];
                    onChange(key, next);
                  }}
                  disabled={isSaving}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors border ${
                    selected
                      ? 'bg-[#338edc]/10 text-[#338edc] border-[#338edc]/40'
                      : 'text-[var(--text-secondary)] border-[var(--border-default,#E5E7EB)] hover:border-[var(--border-hover)]'
                  }`}
                  style={{ background: selected ? undefined : 'var(--bg-card, #fff)' }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        ) : (
          <input
            type="text"
            value={value ?? ''}
            onChange={(e) => onChange(key, e.target.value)}
            disabled={isSaving}
            className="w-full px-3 py-1.5 text-sm rounded-lg border"
            style={{ borderColor: 'var(--border-default, #E5E7EB)', background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
          />
        )}
        {isSaving && (
          <div className="text-[10px] text-[#338edc] mt-1">{t('common.saving', 'Speichert...')}</div>
        )}
      </div>
    </div>
  );
}
